import { useState } from 'react';
import { Link } from 'react-router-dom';

interface Recommendation {
  slug: string;
  title: string;
  icon: string;
  reason: string;
  time: string;
  level: string;
}

const quizData = {
  challenges: [
    { label: 'Ansiedade / Controle emocional', icon: 'ri-emotion-sad-line', slug: 'ancoragem-emocional', title: 'Ancoragem Emocional' },
    { label: 'Crenças limitantes / Sabotagem', icon: 'ri-mental-health-line', slug: 'tecnica-swish-pnl', title: 'Técnica Swish' },
    { label: 'Carreira / Prosperidade financeira', icon: 'ri-money-dollar-circle-line', slug: 'prosperidade-carreira', title: 'Prosperidade e Carreira' },
    { label: 'Relacionamentos / Amor', icon: 'ri-heart-3-line', slug: 'amor-ideal', title: 'Amor Ideal' },
    { label: 'Falta de propósito / Autoconhecimento', icon: 'ri-compass-3-line', slug: 'proposito-vida', title: 'Propósito de Vida' },
    { label: 'Saúde / Bem-estar', icon: 'ri-heart-pulse-line', slug: 'saude-bem-estar', title: 'Saúde e Bem-Estar' },
  ],
  times: [
    { label: '5–10 minutos', value: 'short' },
    { label: '10–20 minutos', value: 'medium' },
    { label: '20 minutos ou mais', value: 'long' },
  ],
  levels: [
    { label: 'Nenhuma — primeira vez', value: 'beginner' },
    { label: 'Alguma — já pratiquei antes', value: 'intermediate' },
    { label: 'Bastante — uso regularmente', value: 'advanced' },
  ],
};

const exerciseMetadata: Record<string, { time: string; level: string; description: string; icon: string }> = {
  'ancoragem-emocional': { time: '15 min', level: 'Iniciante', description: 'Crie gatilhos instantâneos para acessar estados emocionais positivos sob demanda.', icon: 'ri-anchor-line' },
  'tecnica-swish-pnl': { time: '10 min', level: 'Iniciante', description: 'Substitua padrões mentais limitantes por comportamentos de alta performance.', icon: 'ri-refresh-line' },
  'prosperidade-carreira': { time: '20 min', level: 'Iniciante', description: 'Manifeste abundância e sucesso profissional através da reprogramação mental.', icon: 'ri-money-dollar-circle-line' },
  'amor-ideal': { time: '25 min', level: 'Iniciante', description: 'Conecte-se com a energia do relacionamento ideal que você merece.', icon: 'ri-heart-3-line' },
  'proposito-vida': { time: '35 min', level: 'Iniciante', description: 'Conecte-se com sua missão única e viva alinhado com seus valores.', icon: 'ri-compass-3-line' },
  'saude-bem-estar': { time: '18 min', level: 'Iniciante', description: 'Manifeste saúde plena através da conexão mente-corpo.', icon: 'ri-heart-pulse-line' },
};

export default function QuizDiagnostico() {
  const [step, setStep] = useState(0);
  const [challengeIdx, setChallengeIdx] = useState<number | null>(null);
  const [timeValue, setTimeValue] = useState<string | null>(null);
  const [levelValue, setLevelValue] = useState<string | null>(null);
  const [recommendation, setRecommendation] = useState<Recommendation | null>(null);
  const [visible, setVisible] = useState(false);

  const handleChallengeSelect = (idx: number) => {
    setChallengeIdx(idx);
    setStep(1);
  };

  const handleTimeSelect = (val: string) => {
    setTimeValue(val);
    setStep(2);
  };

  const handleLevelSelect = (val: string) => {
    setLevelValue(val);
    if (challengeIdx !== null) {
      const chosen = quizData.challenges[challengeIdx];
      const meta = exerciseMetadata[chosen.slug];
      setRecommendation({
        slug: chosen.slug,
        title: chosen.title,
        icon: meta.icon,
        reason: meta.description,
        time: meta.time,
        level: meta.level,
      });
      setStep(3);
    }
  };

  const handleReset = () => {
    setStep(0);
    setChallengeIdx(null);
    setTimeValue(null);
    setLevelValue(null);
    setRecommendation(null);
  };

  if (!visible) {
    return (
      <div className="py-10 px-6 bg-cream-50 border-t border-gold-100">
        <div className="max-w-3xl mx-auto text-center">
          <button
            onClick={() => setVisible(true)}
            className="inline-flex items-center gap-3 px-8 py-3.5 bg-charcoal-900 text-cream-50 rounded-full text-sm font-semibold tracking-wide hover:bg-charcoal-800 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-search-eye-line text-gold-400 text-base"></i>
            Não sabe por onde começar? Faça o diagnóstico rápido
            <i className="ri-arrow-right-line text-sm"></i>
          </button>
        </div>
      </div>
    );
  }

  return (
    <section className="py-16 px-6 bg-[#faf8f4] border-t border-gold-200/60">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold-100 border border-gold-200 text-gold-700 rounded-full text-xs font-semibold tracking-widest uppercase mb-5">
            <i className="ri-search-eye-line"></i>
            Quiz de Autodiagnóstico
          </div>
          <h2 className="font-display text-3xl font-bold text-charcoal-900 mb-3">
            Qual exercício é ideal para você?
          </h2>
          <p className="text-charcoal-500 text-sm font-light">
            3 perguntas rápidas — recomendação personalizada em segundos
          </p>
          {/* Progress */}
          {step < 3 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i <= step ? 'bg-gold-500 w-10' : 'bg-gold-200 w-6'
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Step 0: Challenge */}
        {step === 0 && (
          <div>
            <p className="text-center text-charcoal-700 font-medium mb-6">
              1 de 3 — Qual é seu maior desafio agora?
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {quizData.challenges.map((c, i) => (
                <button
                  key={i}
                  onClick={() => handleChallengeSelect(i)}
                  className="flex items-center gap-3 px-5 py-4 bg-white border border-gold-200/60 rounded-xl hover:border-gold-400 hover:bg-gold-50 transition-all text-left group cursor-pointer"
                >
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-gold-50 group-hover:bg-gold-100 transition-colors flex-shrink-0">
                    <i className={`${c.icon} text-gold-600 text-lg`}></i>
                  </div>
                  <span className="text-sm font-medium text-charcoal-700 group-hover:text-charcoal-900">{c.label}</span>
                  <i className="ri-arrow-right-line ml-auto text-gold-300 group-hover:text-gold-500 text-sm"></i>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: Time */}
        {step === 1 && (
          <div>
            <p className="text-center text-charcoal-700 font-medium mb-6">
              2 de 3 — Quanto tempo você tem disponível agora?
            </p>
            <div className="grid sm:grid-cols-3 gap-3">
              {quizData.times.map((t) => (
                <button
                  key={t.value}
                  onClick={() => handleTimeSelect(t.value)}
                  className="flex flex-col items-center gap-2 px-5 py-6 bg-white border border-gold-200/60 rounded-xl hover:border-gold-400 hover:bg-gold-50 transition-all cursor-pointer group"
                >
                  <i className="ri-time-line text-2xl text-gold-500 group-hover:text-gold-600"></i>
                  <span className="text-sm font-semibold text-charcoal-800">{t.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Level */}
        {step === 2 && (
          <div>
            <p className="text-center text-charcoal-700 font-medium mb-6">
              3 de 3 — Qual é sua experiência com PNL?
            </p>
            <div className="grid sm:grid-cols-3 gap-3">
              {quizData.levels.map((l) => (
                <button
                  key={l.value}
                  onClick={() => handleLevelSelect(l.value)}
                  className="flex flex-col items-center gap-2 px-5 py-6 bg-white border border-gold-200/60 rounded-xl hover:border-gold-400 hover:bg-gold-50 transition-all cursor-pointer group"
                >
                  <i className={`text-2xl text-gold-500 group-hover:text-gold-600 ${
                    l.value === 'beginner' ? 'ri-seedling-line' :
                    l.value === 'intermediate' ? 'ri-plant-line' : 'ri-tree-line'
                  }`}></i>
                  <span className="text-sm font-semibold text-charcoal-800 text-center">{l.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Recommendation */}
        {step === 3 && recommendation && (
          <div className="bg-white border border-gold-200 rounded-2xl overflow-hidden">
            <div className="bg-charcoal-900 px-8 py-6 flex items-center gap-4">
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-gold-500/15 flex-shrink-0">
                <i className={`${recommendation.icon} text-2xl text-gold-400`}></i>
              </div>
              <div>
                <div className="text-gold-400 text-xs font-semibold tracking-widest uppercase mb-1">Recomendado para você</div>
                <h3 className="font-display text-2xl font-bold text-white">{recommendation.title}</h3>
              </div>
            </div>
            <div className="p-8">
              <p className="text-charcoal-600 leading-relaxed mb-6 font-light">{recommendation.reason}</p>
              <div className="flex items-center gap-4 mb-8">
                <span className="flex items-center gap-1.5 text-xs text-charcoal-500">
                  <i className="ri-time-line text-gold-500"></i>
                  {recommendation.time}
                </span>
                <span className="flex items-center gap-1.5 text-xs text-charcoal-500">
                  <i className="ri-bar-chart-line text-gold-500"></i>
                  {recommendation.level}
                </span>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to={`/exercicio-gratis/${recommendation.slug}`}
                  className="flex-1 inline-flex items-center justify-center gap-2 py-3.5 bg-gradient-to-r from-gold-500 to-gold-700 text-charcoal-900 rounded-xl font-bold text-sm tracking-wide hover:from-gold-600 hover:to-gold-800 transition-all whitespace-nowrap"
                >
                  <i className="ri-play-circle-line text-lg"></i>
                  Começar agora
                </Link>
                <button
                  onClick={handleReset}
                  className="flex-1 py-3.5 bg-charcoal-50 text-charcoal-600 rounded-xl text-sm font-medium hover:bg-charcoal-100 transition-all whitespace-nowrap cursor-pointer border border-charcoal-200"
                >
                  Refazer o diagnóstico
                </button>
              </div>
            </div>
          </div>
        )}

        {step < 3 && (
          <div className="text-center mt-6">
            <button
              onClick={handleReset}
              className="text-xs text-charcoal-400 hover:text-charcoal-600 transition-colors cursor-pointer"
            >
              Cancelar
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
